import { isInWindow } from './utils/isInBrowser';
import type { MetricCollector } from './collector/MetricCollector';
import { logger } from './logger';
import type { BaseNetworkHandler } from './network/BaseNetworkHandler';
import type { HistogramMetric, IncrementMetric, InitializeProps, TimerMetric } from './types';
import type { MetricsMap } from './collector/types';

// TODO: This needs to have types to add types to the MetricCollector!
export abstract class BaseClient {
  isInitialized = false;
  timerId?: NodeJS.Timeout;
  heartBeatId?: NodeJS.Timeout;
  protected networkHandler?: BaseNetworkHandler;
  protected metricCollector?: MetricCollector;
  private onIncrement?: (param: IncrementMetric) => IncrementMetric[];
  private onAddHistogram?: (param: HistogramMetric) => HistogramMetric[];
  private onAddTimer?: (param: TimerMetric) => TimerMetric[];

  abstract sendHeartBeatCounter(version: string): void;

  // Initialize SDK instance
  initialize(props: InitializeProps): void {
    const {
      debugMode = false,
      logTimeInterval = 5 * 1000, // By default, flush all events every 5 secs
      onIncrement = (v: IncrementMetric) => [v],
      onAddHistogram = (v: HistogramMetric) => [v],
      onAddTimer = (v: TimerMetric) => [v],
    } = props;

    logger.setDebugMode(debugMode);
    this.networkHandler = props.networkHandler;
    this.metricCollector = this.networkHandler?.config.collector;

    this.onIncrement = onIncrement;
    this.onAddHistogram = onAddHistogram;
    this.onAddTimer = onAddTimer;

    // Set a interval job to flush all events
    this.timerId = setInterval(this.flush, logTimeInterval);

    // Trying to flush all events before leaving the page
    // Note that this is not guaranteed to completed before browser cancels the request
    // because blocking users from leaving current page is not a proper design
    // TODO: might consider using Web Worker
    if (isInWindow()) {
      window.addEventListener('beforeunload', this.flush);
    }

    this.isInitialized = true;
    logger.log('initializing SDK with config', props);
  }

  // Cancel the interval timer and reset metrics
  destroy(): void {
    logger.log('destroying SDK.');
    if (this.timerId) {
      clearInterval(this.timerId);
    }
    if (this.heartBeatId) {
      clearInterval(this.heartBeatId);
    }
    this.isInitialized = false;
    this.metricCollector?.clearMetrics();
    if (isInWindow()) {
      window.removeEventListener('beforeunload', this.flush);
    }
  }

  isReady(): boolean {
    return this.isInitialized;
  }

  // Increment counter value
  increment(metric: IncrementMetric): void {
    if (!this.isInitialized || !this.metricCollector) {
      return;
    }
    for (const mappedMetric of this.onIncrement?.(metric) ?? []) {
      const { metricsName, dimensions, value = 1 } = mappedMetric;
      if (!Number.isInteger(value)) {
        throw new Error('value must be an integer in increment.');
      }
      this.metricCollector.increment({ metricsName, dimensions, value });
      logger.log(
        `increment counter ${metricsName} with dimension `,
        dimensions,
        ' with value ',
        value
      );
    }
  }

  addHistogram(metric: HistogramMetric): void {
    if (!this.isInitialized || !this.metricCollector) {
      return;
    }
    for (const mappedMetric of this.onAddHistogram?.(metric) ?? []) {
      const { metricsName, dimensions, value } = mappedMetric;
      this.metricCollector.addHistogram({ metricsName, dimensions, value });
      logger.log(
        `addHistogram level ${metricsName} with dimension `,
        dimensions,
        ' with value ',
        value
      );
    }
  }

  addTimer(metric: TimerMetric): void {
    if (!this.isInitialized || !this.metricCollector) {
      return;
    }

    for (const mappedMetric of this.onAddTimer?.(metric) ?? []) {
      const { metricsName, dimensions, milliSec } = mappedMetric;
      // From https://wiki.sc-corp.net/display/INF/Realtime+Metrics+At+Snap
      // "Must be non-negative integer with upper limit of 360 Million"
      // Round to be able to support adding floating point numbers. Useful example:
      // Graphene.addTimer({metricsName, millSec: performance.now() - startTime});
      const roundedMilliSec = Math.round(milliSec);

      this.metricCollector.addTimer({ metricsName, dimensions, milliSec: roundedMilliSec });
      logger.log(
        `addTimer timer ${metricsName} with dimension `,
        dimensions,
        ' with milliSec ',
        roundedMilliSec
      );
    }
  }

  /** Return all collected metrics */
  getCollectedMetrics(): MetricsMap<unknown, unknown, unknown> | undefined {
    return this.metricCollector?.metrics;
  }

  /** Whether SDK has any metrics. Return true, if there is no metrics to flush out. */
  isMetricEmpty = (): boolean => !this.metricCollector || this.metricCollector.isEmpty();

  /** Flush out and send all collected metrics */
  flush = (): Promise<void> => {
    if (!this.metricCollector || !this.networkHandler || this.metricCollector.isEmpty()) {
      return Promise.resolve();
    }
    logger.log('flushing metrics', this.metricCollector.metrics);
    const binaryPayload = this.metricCollector.getPayload();
    this.metricCollector.clearMetrics();
    return this.networkHandler.sendRequest(binaryPayload);
  };
}
